import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react"
import Placeholder from "@tiptap/extension-placeholder"
import StarterKit from "@tiptap/starter-kit"
import { EditorContent, useEditor } from "@tiptap/react"
import type { Editor } from "@tiptap/react"
import { Dropdown, Typography } from "antd"
import type { MenuProps } from "antd"
import classNames from "classnames"
import { getMentionResources } from "~/common/media/resourceMentions"
import styles from "./index.css"
import { createResourceMentionExtension } from "./mentionExtension"
import {
  createPromptFieldExtension,
  REPLACE_PROMPT_DOCUMENT,
  unwrapPromptFields,
} from "./fieldExtension"
import { renderMentionOption } from "./mentionRenderers"
import {
  INITIAL_SUGGESTION_STATE,
  buildEditorContent,
  getEditorPlainText,
  getMentionNodeResources,
  getSuggestionPosition,
  insertMentionResource,
  replaceMentionResource,
  syncMentionResources,
  toMentionAttrs,
} from "./mentionUtils"
import type {
  AssetPromptInputProps,
  AssetPromptInputRef,
  MentionTokenClickHandler,
  SuggestionState,
  SuggestionStateUpdater,
} from "./types"

const { Text } = Typography

const isDropdownTarget = (target: Node) => {
  let element = target instanceof Element ? target : target.parentElement

  while (element) {
    if (element.classList.contains(styles.dropdownOverlay)) {
      return true
    }

    element = element.parentElement
  }

  return false
}

export const AssetPromptInput = forwardRef<
  AssetPromptInputRef,
  AssetPromptInputProps
>(
  (
    {
      className,
      mentionableResources = [],
      placeholder,
      readOnly = false,
      onMentionResourcesChange,
      onTextChange,
      value = "",
      document: promptDocument,
      onDocumentChange,
      onSelectionChange,
      restoreDeletedFields = true,
    },
    ref,
  ) => {
    const containerRef = useRef<HTMLDivElement | null>(null)
    const placeholderRef = useRef(placeholder)
    const activeMenuItemRef = useRef<HTMLDivElement | null>(null)
    const suggestionStateRef = useRef<SuggestionState | null>(null)
    const mentionableResourcesRef = useRef(mentionableResources)
    const mentionableResourceKey = getMentionResources(mentionableResources)
      .map((resource) => resource.mentionId)
      .join("\n")
    const syncedMentionableResourceKeyRef = useRef(mentionableResourceKey)
    const syncedMentionableResourcesRef = useRef(mentionableResources)
    const onMentionResourcesChangeRef = useRef(onMentionResourcesChange)
    const onTextChangeRef = useRef(onTextChange)
    const onDocumentChangeRef = useRef(onDocumentChange)
    const onSelectionChangeRef = useRef(onSelectionChange)
    onSelectionChangeRef.current = onSelectionChange
    const lastDocumentRef = useRef<string | null>(null)
    const mentionResourceKeyRef = useRef("")
    const [suggestionState, setSuggestionState] = useState<SuggestionState>(
      INITIAL_SUGGESTION_STATE,
    )

    const updateSuggestionState = (updater: SuggestionStateUpdater) => {
      setSuggestionState((currentValue) => {
        const nextValue =
          typeof updater === "function" ? updater(currentValue) : updater
        suggestionStateRef.current = nextValue
        return nextValue
      })
    }

    const getCurrentMentionResources = (currentEditor: Editor) => {
      const allowedResourceIds = new Set(
        getMentionResources(mentionableResourcesRef.current).map(
          (resource) => resource.mentionId,
        ),
      )

      return getMentionNodeResources(currentEditor).filter((resource) => {
        const resourceId = resource.id || resource.url

        return allowedResourceIds.has(resourceId)
      })
    }

    const emitEditorChange = (currentEditor: Editor) => {
      const document = currentEditor.getJSON()
      lastDocumentRef.current = JSON.stringify(document)
      onDocumentChangeRef.current?.(document)
      const text = getEditorPlainText(currentEditor)
      const resources = getCurrentMentionResources(currentEditor)
      const resourceKey = resources
        .map((resource) => resource.id || resource.url)
        .join("\n")

      onTextChangeRef.current?.(text)

      if (resourceKey === mentionResourceKeyRef.current) return

      mentionResourceKeyRef.current = resourceKey
      onMentionResourcesChangeRef.current?.(resources)
    }

    const closeSuggestionMenu = () => {
      updateSuggestionState(INITIAL_SUGGESTION_STATE)
    }

    const openMentionResourceMenu: MentionTokenClickHandler = ({
      anchorElement,
      editor: currentEditor,
      mentionId,
      position,
    }) => {
      if (currentEditor.isDestroyed || !currentEditor.isEditable) return

      const container = containerRef.current
      const anchorRect = anchorElement.getBoundingClientRect()

      if (!container) return

      const items = getMentionResources(mentionableResourcesRef.current)
      const currentResourceIndex = items.findIndex((resource) => {
        return resource.mentionId === mentionId
      })

      updateSuggestionState({
        activeIndex: Math.max(currentResourceIndex, 0),
        command: (resource) => {
          replaceMentionResource(
            currentEditor,
            position,
            toMentionAttrs(resource),
          )
        },
        items,
        open: true,
        position: getSuggestionPosition(container, anchorRect),
      })
    }

    useEffect(() => {
      mentionableResourcesRef.current = mentionableResources
    }, [mentionableResources])

    useEffect(() => {
      onMentionResourcesChangeRef.current = onMentionResourcesChange
    }, [onMentionResourcesChange])

    useEffect(() => {
      onTextChangeRef.current = onTextChange
    }, [onTextChange])

    useEffect(() => {
      onDocumentChangeRef.current = onDocumentChange
    }, [onDocumentChange])

    const editor = useEditor({
      immediatelyRender: true,
      extensions: [
        StarterKit.configure({
          blockquote: false,
          bold: false,
          bulletList: false,
          code: false,
          codeBlock: false,
          heading: false,
          horizontalRule: false,
          italic: false,
          orderedList: false,
          strike: false,
        }),
        Placeholder.configure({
          placeholder: ({ editor: currentEditor }) => {
            // Empty inline fields still render their own labels and highlights.
            let hasInlineContent = false
            currentEditor.state.doc.descendants((node) => {
              if (node.isInline) hasInlineContent = true
              return !hasInlineContent
            })
            return hasInlineContent ? "" : placeholderRef.current
          },
        }),
        createPromptFieldExtension(styles.promptField, {
          restoreDeleted: restoreDeletedFields,
        }),
        createResourceMentionExtension({
          containerRef,
          mentionableResourcesRef,
          onMentionClick: openMentionResourceMenu,
          suggestionStateRef,
          updateSuggestionState,
        }),
      ],
      content:
        promptDocument ??
        buildEditorContent({
          mentionableResources,
          value,
        }),
      editable: !readOnly,
      editorProps: {
        attributes: {
          role: "textbox",
          "aria-label": placeholder,
          "aria-multiline": "true",
          "aria-readonly": String(readOnly),
        },
      },
      onCreate: ({ editor: currentEditor }) => {
        emitEditorChange(currentEditor)
      },
      onSelectionUpdate: ({ editor: currentEditor }) => {
        const notify = onSelectionChangeRef.current
        if (!notify) return
        const { from, to, empty } = currentEditor.state.selection
        if (empty || !currentEditor.isEditable) {
          notify(null)
          return
        }
        const start = currentEditor.view.coordsAtPos(from)
        let hasField = false
        currentEditor.state.doc.nodesBetween(from, to, (node) => {
          if (node.type.name === "promptField") hasField = true
          return !hasField
        })
        notify({
          text: currentEditor.state.doc.textBetween(from, to, "\n"),
          hasField,
          rect: { left: start.left, top: start.top },
        })
      },
      onUpdate: ({ editor: currentEditor }) => {
        // Undo can restore a removed resource; reconcile it against the live list.
        if (
          promptDocument &&
          syncMentionResources(
            currentEditor,
            [],
            mentionableResourcesRef.current,
            false,
          )
        )
          return
        emitEditorChange(currentEditor)
      },
    })

    useLayoutEffect(() => {
      placeholderRef.current = placeholder
      if (!editor || editor.isDestroyed) return

      // 刷新占位提示，保留编辑器内容、选区和撤销历史。
      editor.view.dispatch(editor.state.tr.setMeta("addToHistory", false))
    }, [editor, placeholder])

    useImperativeHandle(
      ref,
      () => ({
        focus: () => {
          editor?.chain().focus().run()
        },
        focusField: (name) => {
          if (!editor || editor.isDestroyed || !editor.isEditable) return
          let position: number | undefined
          editor.state.doc.descendants((node, offset) => {
            if (position !== undefined) return false
            if (node.type.name === "promptField" && node.attrs.name === name) {
              position = offset
              return false
            }
          })
          if (position === undefined) return
          editor
            .chain()
            .setTextSelection(position + 1)
            .scrollIntoView()
            .run()
          editor.view.focus()
          const caret = editor.view.domAtPos(position + 1)
          editor.view.dom.ownerDocument
            .getSelection()
            ?.collapse(caret.node, caret.offset)
          const element = editor.view.nodeDOM(position)
          if (element instanceof HTMLElement) {
            element.scrollIntoView({ block: "center", inline: "nearest" })
          }
        },
        setSelectionUnderline: (underline) => {
          if (!editor || editor.isDestroyed || !editor.isEditable) return
          const chain = editor.chain().focus()
          ;(underline
            ? chain.setMark("underline")
            : chain.unsetMark("underline")
          ).run()
        },
        wrapSelectionAsField: (name) => {
          if (!editor || editor.isDestroyed || !editor.isEditable)
            return "编辑器不可用"
          const { state } = editor
          const { from, to, $from, $to } = state.selection
          if (from === to) return "请先选中要设为变量的文字"
          if (!$from.sameParent($to) || $from.parent.type.name !== "paragraph")
            return "请在同一段内选择文字，且不要包含已有变量"
          let hasAtom = false
          state.doc.nodesBetween(from, to, (node) => {
            if (node.isInline && !node.isText) hasAtom = true
          })
          if (hasAtom) return "选中内容不能包含变量或素材引用"
          const text = state.doc.textBetween(from, to)
          if (!text.trim()) return "请先选中要设为变量的文字"
          // 同名变量共用内容：沿用已有变量框的内容
          let existing: typeof $from.parent | undefined
          state.doc.descendants((node) => {
            if (existing) return false
            if (node.type.name === "promptField" && node.attrs.name === name)
              existing = node
          })
          const underline = state.schema.marks.underline
          const field = state.schema.nodes.promptField.create(
            { name, label: name, required: false, maxLength: 0 },
            existing ? existing.content : state.schema.text(text),
            underline && state.doc.rangeHasMark(from, to, underline)
              ? [underline.create()]
              : [],
          )
          editor.view.dispatch(
            state.tr.replaceRangeWith(from, to, field).scrollIntoView(),
          )
          editor.view.focus()
          return ""
        },
        unwrapSelectionFields: () => {
          if (!editor || editor.isDestroyed || !editor.isEditable) return false
          const { from, to } = editor.state.selection
          const tr = unwrapPromptFields(editor.state, from, to)
          if (!tr) return false
          editor.view.dispatch(tr.scrollIntoView())
          editor.view.focus()
          return true
        },
        openMentionMenu: (anchorElement) => {
          if (!editor || editor.isDestroyed) {
            return
          }

          const container = containerRef.current
          const anchorRect = anchorElement?.getBoundingClientRect()

          if (!container || !anchorRect) {
            editor.chain().focus().run()
            return
          }

          updateSuggestionState({
            activeIndex: 0,
            command: (resource) => {
              insertMentionResource(editor, toMentionAttrs(resource))
            },
            items: getMentionResources(mentionableResourcesRef.current),
            open: true,
            position: getSuggestionPosition(container, anchorRect),
          })
          editor.commands.focus()
        },
      }),
      [editor],
    )

    useEffect(() => {
      suggestionStateRef.current = suggestionState
    }, [suggestionState])

    useEffect(() => {
      if (!suggestionState.open) return

      const handlePointerDown = (event: PointerEvent) => {
        const target = event.target

        if (!(target instanceof Node)) return

        if (isDropdownTarget(target)) return

        closeSuggestionMenu()
      }

      document.addEventListener("pointerdown", handlePointerDown)

      return () => {
        document.removeEventListener("pointerdown", handlePointerDown)
      }
    }, [suggestionState.open])

    const activeMentionId =
      suggestionState.items[suggestionState.activeIndex]?.mentionId

    useLayoutEffect(() => {
      if (!suggestionState.open) return

      activeMenuItemRef.current?.scrollIntoView({
        block: "nearest",
        inline: "nearest",
      })
    }, [activeMentionId, suggestionState.open])

    useEffect(() => {
      if (!editor) return
      if (editor.isDestroyed) return

      editor.setEditable(!readOnly)
    }, [editor, readOnly])

    useEffect(() => {
      if (!editor) return
      if (editor.isDestroyed) return

      const currentValue = getEditorPlainText(editor)
      const mentionableResourcesChanged =
        syncedMentionableResourceKeyRef.current !== mentionableResourceKey
      const previousMentionableResources = syncedMentionableResourcesRef.current

      if (promptDocument) {
        syncedMentionableResourceKeyRef.current = mentionableResourceKey
        syncedMentionableResourcesRef.current = mentionableResources
        const serialized = JSON.stringify(promptDocument)
        if (serialized !== lastDocumentRef.current) {
          editor
            .chain()
            .setMeta(REPLACE_PROMPT_DOCUMENT, true)
            .setContent(promptDocument, { emitUpdate: false })
            .run()
        }
        if (!syncMentionResources(editor, [], mentionableResources, false)) {
          if (
            serialized !== lastDocumentRef.current ||
            mentionableResourcesChanged
          ) {
            emitEditorChange(editor)
          }
        }
        return
      }

      if (currentValue === value && !mentionableResourcesChanged) return

      syncedMentionableResourceKeyRef.current = mentionableResourceKey
      syncedMentionableResourcesRef.current = mentionableResources

      if (currentValue !== value) {
        editor.commands.setContent(
          buildEditorContent({
            mentionableResources,
            value,
          }),
          {
            emitUpdate: false,
          },
        )
      } else if (
        mentionableResourcesChanged &&
        syncMentionResources(
          editor,
          previousMentionableResources,
          mentionableResources,
        )
      ) {
        return
      }

      emitEditorChange(editor)
    }, [
      editor,
      mentionableResourceKey,
      mentionableResources,
      value,
      promptDocument,
    ])

    const menuItems: NonNullable<MenuProps["items"]> =
      suggestionState.items.length > 0
        ? suggestionState.items.map((item, index) => {
            return {
              key: item.mentionId,
              label: (
                <div
                  ref={
                    index === suggestionState.activeIndex
                      ? activeMenuItemRef
                      : undefined
                  }
                >
                  {renderMentionOption(
                    item,
                    index === suggestionState.activeIndex,
                  )}
                </div>
              ),
            }
          })
        : [
            {
              key: "empty",
              disabled: true,
              label: (
                <div className={styles.menuEmpty}>
                  <Text type="secondary">没有可引用的素材</Text>
                </div>
              ),
            },
          ]

    return (
      <div
        ref={containerRef}
        className={classNames(styles.root, className)}
      >
        <EditorContent
          editor={editor}
          className={styles.editor}
        />

        {suggestionState.open ? (
          <Dropdown
            menu={{
              items: menuItems,
              onClick: ({ key }) => {
                const selectedItem = suggestionState.items.find((item) => {
                  return item.mentionId === key
                })

                if (!selectedItem) return

                suggestionState.command?.(selectedItem)
                closeSuggestionMenu()
                editor?.commands.focus()
              },
              selectable: true,
              selectedKeys: [
                suggestionState.items[suggestionState.activeIndex]?.mentionId ||
                  "",
              ],
            }}
            classNames={{
              root: styles.dropdownOverlay,
            }}
            open
          >
            <div
              className={styles.dropdownAnchor}
              onMouseDown={(event) => {
                event.preventDefault()
              }}
              style={{
                left: suggestionState.position.left,
                top: suggestionState.position.top,
              }}
            />
          </Dropdown>
        ) : null}
      </div>
    )
  },
)
